import { apiClient } from './client'
import { API_ENDPOINTS } from '@/utils/constants'
import type {
  ContractTransaction,
  ContractTransactionFilters,
  ContractTransactionsPage,
  TransactionDirection,
} from '@/types/billing'
import type { PaginatedResponse } from '@/types'

type ApiResponse<T> = { message?: string; data?: T }

/** Laravel AnonymousResourceCollection + paginate: data + meta (без legacy top-level current_page). */
type IndexResponse<T> = {
  data: T[]
  meta: {
    current_page: number
    last_page: number
    per_page: number
    total: number
  }
}

function unwrapPaginated<T>(raw: IndexResponse<T>): PaginatedResponse<T> {
  return {
    data: raw.data,
    current_page: raw.meta.current_page,
    last_page: raw.meta.last_page,
    per_page: raw.meta.per_page,
    total: raw.meta.total,
  }
}

function unwrapResource<T>(raw: ApiResponse<T> | T): T {
  if (raw && typeof raw === 'object' && 'data' in raw) {
    return (raw as ApiResponse<T>).data as T
  }
  return raw as T
}

export const contractTransactionsApi = {
  index: async (
    contractId: number,
    filters: ContractTransactionFilters = {}
  ): Promise<ContractTransactionsPage> => {
    const response = await apiClient.get<IndexResponse<ContractTransaction>>(
      API_ENDPOINTS.CONTRACTS.TRANSACTIONS(contractId),
      { params: filters }
    )
    return unwrapPaginated(response.data)
  },

  topup: async (
    contractId: number,
    payload: { amount: number; currency?: string; description?: string }
  ): Promise<ContractTransaction> => {
    const response = await apiClient.post<ApiResponse<ContractTransaction>>(
      API_ENDPOINTS.CONTRACTS.TRANSACTIONS_TOPUP(contractId),
      payload
    )
    return unwrapResource(response.data)
  },

  correction: async (
    contractId: number,
    payload: {
      amount: number
      direction: TransactionDirection
      currency?: string
      description?: string
      parent_transaction_id?: number
    }
  ): Promise<ContractTransaction> => {
    const response = await apiClient.post<ApiResponse<ContractTransaction>>(
      API_ENDPOINTS.CONTRACTS.TRANSACTIONS_CORRECTION(contractId),
      payload
    )
    return unwrapResource(response.data)
  },

  studentRequest: async (
    contractId: number,
    payload: { amount: number; currency?: string; description: string }
  ): Promise<ContractTransaction> => {
    const response = await apiClient.post<ApiResponse<ContractTransaction>>(
      API_ENDPOINTS.CONTRACTS.TRANSACTIONS_STUDENT_REQUEST(contractId),
      payload
    )
    return unwrapResource(response.data)
  },

  approve: async (transactionId: number): Promise<ContractTransaction> => {
    const response = await apiClient.post<ApiResponse<ContractTransaction>>(
      API_ENDPOINTS.CONTRACT_TRANSACTIONS.APPROVE(transactionId)
    )
    return unwrapResource(response.data)
  },

  reject: async (transactionId: number, reason?: string): Promise<ContractTransaction> => {
    const response = await apiClient.post<ApiResponse<ContractTransaction>>(
      API_ENDPOINTS.CONTRACT_TRANSACTIONS.REJECT(transactionId),
      reason ? { reason } : {}
    )
    return unwrapResource(response.data)
  },
}
